import React, { useState, useEffect } from "react";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { auth, db } from "../firebase";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { VscAccount } from "react-icons/vsc";

export default function Dashboard({ theme, user: _user }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Use prop or fallback to currentUser
  const user = _user || auth.currentUser;

  useEffect(() => {
    async function fetchSessions() {
      if (!user) return;
      const uid = user.uid;
      const sessionsRef = collection(db, "users", uid, "sessions");
      const q = query(sessionsRef, orderBy("startTime", "desc"), limit(1000));
      const snapshot = await getDocs(q);
      setSessions(snapshot.docs.map(doc => doc.data()));
      setLoading(false);
    }
    fetchSessions();
  }, [user]);

  // a. Total time
  const totalSeconds = sessions.reduce((acc, s) => acc + (s.duration || 0), 0);

  // b. Last 7 days (grouped)
  const now = new Date();
  const dayStats = {};
  for (let i = 6; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(now.getDate() - i);
    const key = date.toISOString().slice(0, 10);
    dayStats[key] = 0;
  }
  sessions.forEach(s => {
    if (s.date in dayStats) dayStats[s.date] += s.duration;
  });

  // c. Average session
  const avgSession = sessions.length > 0 ? Math.round(totalSeconds / sessions.length) : 0;

  // Helper for time formatting
  function formatTime(secs) {
    if (!secs || secs < 60) return `${secs || 0}s`;
    const h = Math.floor(secs / 3600),
      m = Math.floor((secs % 3600) / 60),
      s = secs % 60;
    return h ? `${h}h ${m}m` : `${m}m ${s}s`;
  }

  if (loading) return <div>Loading stats...</div>;

  // --- HEADER UI, like Monkeytype ---
  return (
    <div
      className="min-h-screen"
      style={{
        color: theme?.colors?.text,
        background: theme?.colors?.background,
        fontFamily: theme?.fontFamily,
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <div
        className="flex justify-center items-center py-10"
        style={{
          background: theme?.colors?.background,
          minHeight: 140,
        }}
      >
        <div
          className="flex flex-row items-center gap-10 px-10 py-7 rounded-2xl shadow"
          style={{
            background: theme?.colors?.logoBackground,
            color: theme?.colors?.logoText,
            minWidth: 760,
            maxWidth: 1024,
            width: "100%",
          }}
        >
          {/* Profile */}
          <div className="flex flex-col items-center pr-10">
                    <div
            className="rounded-full flex items-center justify-center mb-2"
            style={{
                width: 68,
                height: 68,
                background: theme?.colors?.background,
                color: theme?.colors?.logoText,
            }}
            >
            <VscAccount size={44} />
            </div>
            <div
              className="text-xs mt-1"
              style={{ color: theme?.colors?.timerText, opacity: 0.65 }}
            >
              Joined {/* can show joined date here if you want */}
            </div>
          </div>

          {/* Divider */}
          <div
            style={{
              width: 2,
              height: 56,
              background: theme?.colors?.background,
              opacity: 0.1,
            }}
          />

          {/* Stats Row */}
          <div className="flex-1 flex flex-row justify-between items-center gap-10">
            {/* Timers Started */}
            <div className="flex flex-col items-center px-6">
              <div
                className="uppercase text-base font-semibold mb-1"
                style={{ color: theme?.colors?.timerText, opacity: 0.7 }}
              >
                timers started
              </div>
              <div className="text-3xl font-bold">{sessions.length}</div>
            </div>
            {/* Average Timer */}
            <div className="flex flex-col items-center px-6">
              <div
                className="uppercase text-base font-semibold mb-1"
                style={{ color: theme?.colors?.timerText, opacity: 0.7 }}
              >
                avg timer
              </div>
              <div className="text-3xl font-bold">{formatTime(avgSession)}</div>
            </div>
            {/* Total Time */}
            <div className="flex flex-col items-center px-6">
              <div
                className="uppercase text-base font-semibold mb-1"
                style={{ color: theme?.colors?.timerText, opacity: 0.7 }}
              >
                total time
              </div>
              <div className="text-3xl font-bold">{formatTime(totalSeconds)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Rest of dashboard --- */}
      <div className="max-w-2xl mx-auto p-6">
        <div
          className="rounded-lg p-4 mb-4"
          style={{
            background: theme?.colors?.logoBackground,
            color: theme?.colors?.logoText,
          }}
        >
          <div className="text-lg font-semibold mb-2" style={{ color: theme?.colors?.timerText }}>
            Last 7 Days
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={Object.entries(dayStats).map(([date, seconds]) => ({
                date: date.slice(5), // MM-DD
                minutes: Math.round(seconds / 60),
              }))}
            >
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="minutes" fill={theme?.colors?.timerText || "#90CFFB"} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-6">
          <h3
            className="text-lg font-semibold mb-2"
            style={{ color: theme?.colors?.timerText }}
          >
            Session History (Recent)
          </h3>
          <ul className="space-y-2">
            {sessions.slice(0, 10).map((s, i) => (
              <li
                key={i}
                className="rounded p-3 flex items-center justify-between"
                style={{
                  background: theme?.colors?.logoBackground,
                  color: theme?.colors?.logoText,
                }}
              >
                <div>
                  <span className="font-mono">{s.date}</span>
                  {" • "}
                  <span>{formatTime(s.duration)}</span>
                </div>
                <span
                  className="text-xs px-2 py-1 rounded"
                  style={{
                    background: theme?.colors?.timerText,
                    color: theme?.colors?.logoText,
                  }}
                >
                  {s.timerType}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
